import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, take } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { PrimengExportsModule } from '../primeng-exports.module';
import {
  ExternalService,
  type SecretKeyError
} from '../services/data-service/external.service';
import { AuthService } from '../services/auth.service';
import { EventStateService } from '../services/state-service/event-state.service';
import { FileSaverService } from '../services/file-saver.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-secret-key-page',
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, PrimengExportsModule, TranslocoPipe],
  templateUrl: './secret-key-page.component.html',
  styleUrls: ['./secret-key-page.component.scss']
})
export class SecretKeyPageComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly externalService = inject(ExternalService);
  private readonly authService = inject(AuthService);
  eventStateService = inject(EventStateService);
  private readonly fileSaverService = inject(FileSaverService);

  private readonly destroy$ = new Subject<void>();
  siNumber?: string | number | null;
  siDetailId?: string | number | null;
  secretKey: string = '';
  secretKeyError?: SecretKeyError;
  nextPage = false;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const si: unknown = params['si_numb'];
        const id: unknown = params['sidetail_id'];
        this.siNumber =
          typeof si === 'string' || typeof si === 'number' || si === null
            ? si
            : undefined;
        this.siDetailId =
          typeof id === 'string' || typeof id === 'number' || id === null
            ? id
            : undefined;
      });
  }

  checkValidity() {
    this.secretKeyError = undefined;
    this.externalService
      .getVerifySiNumbSecretKey(this.siNumber, this.siDetailId, this.secretKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.valid) {
          this.nextPage = res.valid;
          this.authService.offPlatformSecretKey = this.secretKey;
        } else {
          this.secretKeyError = res.reason ?? 'invalid';
        }
      });
  }

  downloadGeoJsonFiles(): void {
    if (this.authService.offPlatformSecretKey) {
      this.externalService
        .downloadGeoJSONFiles(this.siNumber, this.siDetailId)
        .pipe(take(1))
        .subscribe((res) => {
          const blob = new Blob([res], { type: 'application/json' });
          this.fileSaverService.saveAs(
            blob,
            `${this.siNumber}_geojson_file.geojson`
          );
        });
    }
  }

  downloadEUDRPackage() {
    if (this.authService.offPlatformSecretKey) {
      this.externalService
        .downloadEudrPackage(this.siNumber, this.siDetailId)
        .pipe(take(1))
        .subscribe((res) => {
          const blob = new Blob([res], { type: 'application/zip' });
          this.fileSaverService.saveAs(
            blob,
            `${this.siNumber}_EUDR_package.zip`
          );
        });
    }
  }

  logout() {
    this.nextPage = false;
    this.authService.offPlatformSecretKey = null;
  }
}
